
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { SubscriptionService, SubscriptionStatus } from "@/services/subscription";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { SubscriptionHeader } from "./subscription/SubscriptionHeader";
import { UpgradePlans } from "./subscription/UpgradePlans";
import { ActiveSubscription } from "./subscription/ActiveSubscription";
import { CancelSubscriptionDialog } from "./subscription/CancelSubscriptionDialog";

interface SubscriptionManagementProps {
  user: any;
}

export const SubscriptionManagement = ({ user }: SubscriptionManagementProps) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [processingCancel, setProcessingCancel] = useState(false);
  const [processingUpgrade, setProcessingUpgrade] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const status = await SubscriptionService.getSubscriptionStatus(user.id);
        setSubscriptionStatus(status);
      } catch (error) {
        console.error("Error fetching subscription:", error);
        toast({
          title: "Error fetching subscription",
          description: "Could not load your subscription information. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user, toast]);

  const handleUpgrade = async (type: 'monthly' | 'yearly') => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setProcessingUpgrade(true);
    try {
      const url = await SubscriptionService.createCheckoutSession(user.id, type);
      
      if (url) {
        setCheckoutUrl(url);
        window.open(url, '_blank');
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: "Failed to start checkout process. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingUpgrade(false);
    }
  };

  const handleCancelSubscription = async () => {
    setProcessingCancel(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription');
      
      if (error) throw error;
      
      toast({
        title: "Subscription Canceled",
        description: "Your subscription has been canceled and will remain active until the end of your billing period.",
      });
      
      // Refresh subscription status
      if (user) {
        const status = await SubscriptionService.getSubscriptionStatus(user.id);
        setSubscriptionStatus(status);
      }
      
      setCancelDialogOpen(false);
    } catch (error: any) {
      console.error("Error canceling subscription:", error);
      toast({
        title: "Error",
        description: "Failed to cancel your subscription. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setProcessingCancel(false);
    }
  };

  const formatSubscriptionType = (type: string) => {
    switch (type) {
      case 'monthly':
        return 'Monthly ($5/month)';
      case 'yearly':
        return 'Yearly ($50/year)';
      default:
        return 'Free';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SubscriptionHeader 
        subscriptionStatus={subscriptionStatus}
        formatSubscriptionType={formatSubscriptionType}
      />

      {subscriptionStatus?.isActive ? (
        <ActiveSubscription onCancel={() => setCancelDialogOpen(true)} />
      ) : (
        <UpgradePlans
          processingUpgrade={processingUpgrade}
          handleUpgrade={handleUpgrade}
          checkoutUrl={checkoutUrl}
        />
      )}
      
      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={handleCancelSubscription}
        processing={processingCancel}
      />
    </div>
  );
};
